import React, { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet";
import PageLayout, {
  PageSection,
} from "../../../components/navigation/PageLayout";
import Icon from "../../../components/AppIcon";
import { useAuth } from "../../../contexts/AuthContext";
import { useTenantContext } from "../../../contexts/TenantContext";
import apiClient from "../../../services/api/client";
import JobRoleModal from "./components/JobRoleModal";
import JobRoleSyncModal from "./components/JobRoleSyncModal";

const JobRolesPage = () => {
  const { isGlobalAdmin, isTenantAdmin } = useAuth();
  const {
    currentTenant,
    selectedTenant,
    needsTenantSelection,
    isGlobalAdmin: isGlobalAdminContext,
  } = useTenantContext();

  const [jobRoles, setJobRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("");
  const [filterEligibility, setFilterEligibility] = useState("all");
  const [showInactive, setShowInactive] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [notification, setNotification] = useState(null);
  const [providers, setProviders] = useState([]);

  // Only fetch data when tenant is selected (or for non-global admins)
  const shouldFetchData = !isGlobalAdminContext || selectedTenant;

  // Fetch job roles using apiClient
  const fetchJobRoles = async () => {
    if (!shouldFetchData) return;

    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (!showInactive) params.activeOnly = true;

      // For global admins, use selected tenant ID
      // For tenant admins, the API will use their tenant from the token
      if (isGlobalAdminContext && selectedTenant?.id) {
        params.tenantId = selectedTenant.id;
      }

      const data = await apiClient.get("/jobroles", params);

      if (data.success) {
        setJobRoles(data.data || []);
      } else {
        throw new Error(data.message || "Failed to fetch job roles");
      }
    } catch (err) {
      setError(err.message || "Failed to fetch job roles");
      console.error("Error fetching job roles:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch available HR providers
  const fetchProviders = async () => {
    try {
      const data = await apiClient.get("/jobroles/providers");
      if (data.success) {
        setProviders(data.data || []);
      }
    } catch (err) {
      console.error("Error fetching providers:", err);
    }
  };

  useEffect(() => {
    if (shouldFetchData) {
      fetchJobRoles();
      fetchProviders();
    }
  }, [showInactive, selectedTenant?.id, shouldFetchData]);

  // Get unique departments for filter
  const departments = useMemo(() => {
    const depts = [
      ...new Set(jobRoles.map((r) => r.department).filter(Boolean)),
    ];
    return depts.sort();
  }, [jobRoles]);

  // Filter job roles
  const filteredRoles = useMemo(() => {
    return jobRoles.filter((role) => {
      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const matchesSearch =
          role.name.toLowerCase().includes(search) ||
          role.code?.toLowerCase().includes(search) ||
          role.department?.toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }

      // Department filter
      if (filterDepartment && role.department !== filterDepartment) {
        return false;
      }

      // Eligibility filter
      if (filterEligibility === "eligible" && !role.isEligibleForTronc) {
        return false;
      }
      if (filterEligibility === "ineligible" && role.isEligibleForTronc) {
        return false;
      }

      return true;
    });
  }, [jobRoles, searchTerm, filterDepartment, filterEligibility]);

  // Group roles by department
  const groupedRoles = useMemo(() => {
    const groups = {};
    filteredRoles.forEach((role) => {
      const dept = role.department || "Uncategorized";
      if (!groups[dept]) {
        groups[dept] = [];
      }
      groups[dept].push(role);
    });
    return groups;
  }, [filteredRoles]);

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleCreateRole = () => {
    setSelectedRole(null);
    setShowRoleModal(true);
  };

  const handleEditRole = (role) => {
    setSelectedRole(role);
    setShowRoleModal(true);
  };

  const handleDeleteRole = async (role) => {
    if (
      !confirm(`Are you sure you want to delete the job role "${role.name}"?`)
    ) {
      return;
    }

    try {
      await apiClient.delete(`/jobroles/${role.id}`);
      showNotification(`Job role "${role.name}" deleted successfully`);
      fetchJobRoles();
    } catch (err) {
      showNotification(err.message || "Failed to delete job role", "error");
    }
  };

  const handleSaveRole = async (roleData) => {
    try {
      const isEdit = !!selectedRole;

      // For global admins creating new roles, include the tenant ID
      if (!isEdit && isGlobalAdminContext && selectedTenant?.id) {
        roleData.tenantId = selectedTenant.id;
      }

      if (isEdit) {
        await apiClient.put(`/jobroles/${selectedRole.id}`, roleData);
      } else {
        await apiClient.post("/jobroles", roleData);
      }

      showNotification(
        `Job role ${isEdit ? "updated" : "created"} successfully`,
      );
      setShowRoleModal(false);
      fetchJobRoles();
    } catch (err) {
      showNotification(err.message || "Failed to save job role", "error");
      throw err;
    }
  };

  const handleSync = async (providerName) => {
    try {
      const params = {};
      if (isGlobalAdminContext && selectedTenant?.id) {
        params.tenantId = selectedTenant.id;
      }

      const data = await apiClient.post(
        `/jobroles/sync/${providerName}`,
        null,
        { params },
      );

      const result = data.data;
      showNotification(
        `Sync completed: ${result.processedRecords} roles synced from ${providerName}`,
        "success",
      );
      setShowSyncModal(false);
      fetchJobRoles();
    } catch (err) {
      showNotification(err.message || "Sync failed", "error");
    }
  };

  const breadcrumbs = [
    { label: "Dashboard", path: "/dashboard-overview" },
    { label: "Company Settings", path: "/system/job-roles" },
    { label: "Job Roles", path: "/system/job-roles" },
  ];

  const canEdit = isGlobalAdmin || isTenantAdmin;

  // Select Tenant Empty State for Global Admins
  if (isGlobalAdminContext && needsTenantSelection) {
    return (
      <>
        <Helmet>
          <title>Job Roles - Rocket Tronc</title>
        </Helmet>
        <PageLayout
          title="Job Roles"
          description="Manage employee job roles and TRONC eligibility settings."
          breadcrumbs={breadcrumbs}
        >
          <div className="card p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Icon name="Building2" size={40} className="text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-3">Select a Tenant</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              As a Global Administrator, please select a tenant from the sidebar
              to manage their job roles.
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Icon name="ArrowLeft" size={16} />
              <span>Use the tenant selector in the sidebar</span>
            </div>
          </div>
        </PageLayout>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Job Roles - {selectedTenant?.name || "Rocket Tronc"}</title>
      </Helmet>

      <PageLayout
        title="Job Roles"
        description={
          selectedTenant
            ? `${selectedTenant.name} - Manage employee job roles and TRONC eligibility settings.`
            : "Manage employee job roles and TRONC eligibility settings. Job roles determine how tips are distributed."
        }
        breadcrumbs={breadcrumbs}
      >
        {/* Notification */}
        {notification && (
          <div
            className={`mb-6 alert ${notification.type === "error" ? "alert-error" : "alert-success"}`}
          >
            <Icon
              name={
                notification.type === "error" ? "AlertCircle" : "CheckCircle"
              }
              size={20}
            />
            <span>{notification.message}</span>
          </div>
        )}

        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row justify-between gap-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative">
              <Icon
                name="Search"
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                type="text"
                placeholder="Search roles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10 w-full sm:w-64"
              />
            </div>

            {/* Department Filter */}
            <select
              value={filterDepartment}
              onChange={(e) => setFilterDepartment(e.target.value)}
              className="input w-full sm:w-48"
            >
              <option value="">All Departments</option>
              {departments.map((dept) => (
                <option key={dept} value={dept}>
                  {dept}
                </option>
              ))}
            </select>

            {/* Eligibility Filter */}
            <select
              value={filterEligibility}
              onChange={(e) => setFilterEligibility(e.target.value)}
              className="input w-full sm:w-48"
            >
              <option value="all">All Eligibility</option>
              <option value="eligible">TRONC Eligible</option>
              <option value="ineligible">Not Eligible</option>
            </select>
          </div>

          <div className="flex gap-2">
            {/* Show Inactive Toggle */}
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="checkbox"
              />
              <span>Show inactive</span>
            </label>

            {/* Sync Button */}
            {canEdit && providers.length > 0 && (
              <button
                onClick={() => setShowSyncModal(true)}
                className="btn btn-outline btn-sm"
              >
                <Icon name="RefreshCw" size={16} />
                Sync from HR
              </button>
            )}

            {/* Add Role Button */}
            {canEdit && (
              <button
                onClick={handleCreateRole}
                className="btn btn-primary btn-sm"
              >
                <Icon name="Plus" size={16} />
                Add Role
              </button>
            )}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="card p-12 text-center">
            <div className="spinner mx-auto mb-4" />
            <p className="text-muted-foreground">Loading job roles...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="alert alert-error mb-6">
            <Icon name="AlertCircle" size={20} />
            <span>{error}</span>
            <button
              onClick={fetchJobRoles}
              className="btn btn-sm btn-outline ml-auto"
            >
              Retry
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && filteredRoles.length === 0 && (
          <div className="card p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <Icon
                name="Briefcase"
                size={32}
                className="text-muted-foreground"
              />
            </div>
            <h3 className="text-lg font-semibold mb-2">No job roles found</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              {searchTerm || filterDepartment || filterEligibility !== "all"
                ? "No roles match your current filters. Try adjusting your search criteria."
                : "Create job roles to categorize employees and set TRONC distribution points."}
            </p>
            {canEdit &&
              !searchTerm &&
              !filterDepartment &&
              filterEligibility === "all" && (
                <div className="flex justify-center gap-3">
                  <button
                    onClick={handleCreateRole}
                    className="btn btn-primary"
                  >
                    <Icon name="Plus" size={16} />
                    Create First Role
                  </button>
                  {providers.length > 0 && (
                    <button
                      onClick={() => setShowSyncModal(true)}
                      className="btn btn-outline"
                    >
                      <Icon name="RefreshCw" size={16} />
                      Sync from HR System
                    </button>
                  )}
                </div>
              )}
          </div>
        )}

        {/* Job Roles List - Grouped by Department */}
        {!loading && !error && filteredRoles.length > 0 && (
          <div className="space-y-6">
            {Object.entries(groupedRoles).map(([department, roles]) => (
              <PageSection
                key={department}
                title={department}
                actions={
                  <span className="text-sm text-muted-foreground">
                    {roles.length} role{roles.length !== 1 ? "s" : ""}
                  </span>
                }
              >
                <div className="divide-y divide-border">
                  {roles.map((role) => (
                    <JobRoleRow
                      key={role.id}
                      role={role}
                      canEdit={canEdit}
                      onEdit={() => handleEditRole(role)}
                      onDelete={() => handleDeleteRole(role)}
                    />
                  ))}
                </div>
              </PageSection>
            ))}
          </div>
        )}

        {/* Summary Stats */}
        {!loading && !error && jobRoles.length > 0 && (
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="card p-4 text-center">
              <p className="text-2xl font-bold text-primary">
                {jobRoles.length}
              </p>
              <p className="text-sm text-muted-foreground">Total Roles</p>
            </div>
            <div className="card p-4 text-center">
              <p className="text-2xl font-bold text-success">
                {jobRoles.filter((r) => r.isEligibleForTronc).length}
              </p>
              <p className="text-sm text-muted-foreground">TRONC Eligible</p>
            </div>
            <div className="card p-4 text-center">
              <p className="text-2xl font-bold text-accent">
                {departments.length}
              </p>
              <p className="text-sm text-muted-foreground">Departments</p>
            </div>
            <div className="card p-4 text-center">
              <p className="text-2xl font-bold text-foreground">
                {jobRoles.reduce((sum, r) => sum + (r.employeeCount || 0), 0)}
              </p>
              <p className="text-sm text-muted-foreground">Total Employees</p>
            </div>
          </div>
        )}

        {/* Modals */}
        {showRoleModal && (
          <JobRoleModal
            role={selectedRole}
            onClose={() => setShowRoleModal(false)}
            onSave={handleSaveRole}
          />
        )}

        {showSyncModal && (
          <JobRoleSyncModal
            providers={providers}
            onClose={() => setShowSyncModal(false)}
            onSync={handleSync}
          />
        )}
      </PageLayout>
    </>
  );
};

// Job Role Row Component
const JobRoleRow = ({ role, canEdit, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`py-4 ${!role.isActive ? "opacity-60" : ""}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 flex-1">
          {/* Color indicator & Name */}
          <div className="flex items-center gap-3">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: role.colorCode || "#6b7280" }}
            />
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-semibold">{role.name}</h4>
                {role.code && (
                  <span className="text-xs text-muted-foreground font-mono bg-muted px-1.5 py-0.5 rounded">
                    {role.code}
                  </span>
                )}
                {!role.isActive && (
                  <span className="badge badge-neutral text-xs">Inactive</span>
                )}
                {role.hrProvider && (
                  <span
                    className="badge badge-outline text-xs"
                    title={`Synced from ${role.hrProvider}`}
                  >
                    <Icon name="Link" size={10} className="mr-1" />
                    {role.hrProvider}
                  </span>
                )}
                {role.isLocallyModified && role.hrProvider && (
                  <span
                    className="badge badge-warning text-xs"
                    title="Modified locally"
                  >
                    Modified
                  </span>
                )}
              </div>
              {role.department && (
                <p className="text-sm text-muted-foreground">
                  {role.department}
                </p>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="hidden sm:flex items-center gap-6 ml-auto mr-4">
            {/* TRONC Eligibility */}
            <div
              className="flex items-center gap-2"
              title={
                role.isEligibleForTronc
                  ? "TRONC Eligible"
                  : "Not TRONC Eligible"
              }
            >
              <Icon
                name={role.isEligibleForTronc ? "CheckCircle" : "XCircle"}
                size={16}
                className={
                  role.isEligibleForTronc
                    ? "text-success"
                    : "text-muted-foreground"
                }
              />
              <span className="text-sm">
                {role.isEligibleForTronc
                  ? `${role.troncPoints} pts`
                  : "Not eligible"}
              </span>
            </div>

            {/* Management Badge */}
            {role.isManagement && (
              <span className="badge badge-secondary text-xs">
                <Icon name="Crown" size={10} className="mr-1" />
                Management
              </span>
            )}

            {/* Employee Count */}
            <div
              className="flex items-center gap-1 text-sm text-muted-foreground"
              title="Employees with this role"
            >
              <Icon name="Users" size={14} />
              <span>{role.employeeCount || 0}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setExpanded(!expanded)}
            className="btn btn-ghost btn-sm"
            title="View details"
          >
            <Icon name={expanded ? "ChevronUp" : "ChevronDown"} size={16} />
          </button>

          {canEdit && (
            <>
              <button
                onClick={onEdit}
                className="btn btn-ghost btn-sm"
                title="Edit role"
              >
                <Icon name="Pencil" size={16} />
              </button>
              <button
                onClick={onDelete}
                className="btn btn-ghost btn-sm text-error"
                title="Delete role"
                disabled={role.employeeCount > 0}
              >
                <Icon name="Trash2" size={16} />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="mt-4 pl-6 border-l-2 border-muted ml-1.5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Department</p>
              <p className="font-medium">{role.department || "None"}</p>
            </div>
            <div>
              <p className="text-muted-foreground">TRONC Points</p>
              <p className="font-medium">{role.troncPoints}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Sort Order</p>
              <p className="font-medium">{role.sortOrder || 0}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Employees</p>
              <p className="font-medium">{role.employeeCount || 0}</p>
            </div>
          </div>

          {role.description && (
            <div className="mt-3">
              <p className="text-muted-foreground text-sm">Description</p>
              <p className="text-sm">{role.description}</p>
            </div>
          )}

          {role.hrProvider && (
            <div className="mt-3 p-3 bg-muted/50 rounded-lg">
              <p className="text-xs text-muted-foreground mb-1">
                HR Integration
              </p>
              <div className="flex items-center gap-4 text-sm">
                <span>
                  <strong>Provider:</strong> {role.hrProvider}
                </span>
                <span>
                  <strong>External ID:</strong> {role.externalId || "N/A"}
                </span>
                {role.lastSyncedOn && (
                  <span>
                    <strong>Last Sync:</strong>{" "}
                    {new Date(role.lastSyncedOn).toLocaleString()}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default JobRolesPage;
